import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API_BASE = `${BACKEND_URL}/api`;

// Create axios instance with default config
const api = axios.create({
  baseURL: API_BASE,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor to add auth token
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('salty_token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Response interceptor for error handling
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('salty_token');
      localStorage.removeItem('salty_user');
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

// Auth APIs
export const authAPI = {
  login: (email, password) => api.post('/auth/login', { email, password }),
  register: (data) => api.post('/auth/register', data),
  getMe: () => api.get('/auth/me'),
  googleLogin: (credential) => api.post('/auth/google', { credential }),
  updateMe: (data) => api.patch('/auth/me', data),
};

// AOP APIs
export const aopAPI = {
  create: (data) => api.post('/aop', data),
  getAll: (params) => api.get('/aop', { params }),
  getById: (id) => api.get(`/aop/${id}`),
  update: (id, data) => api.put(`/aop/${id}`, data),
  getFiscalYears: () => api.get('/aop/fiscal-years'),
  getOverhead: (fiscalYear = 2027) => api.get('/aop/overhead', { params: { fiscal_year: fiscalYear } }),
  getConsolidated: (fiscalYear = 2027) => api.get('/aop/consolidated', { params: { fiscal_year: fiscalYear } }),
  getChannels: (fiscalYear = 2027) => api.get('/aop/channels', { params: { fiscal_year: fiscalYear } }),
  getChannelDetail: (channelKey, fiscalYear = 2027) => api.get(`/aop/channels/${channelKey}`, { params: { fiscal_year: fiscalYear } }),

  // Planning sessions (user edits + versioning)
  previewImport: (file, fiscalYear) => { const fd = new FormData(); fd.append('file', file); return api.post(`/aop/planning/preview-import?fiscal_year=${fiscalYear}`, fd, { headers: { 'Content-Type': 'multipart/form-data' } }); },
  savePlan: (data) => api.post('/aop/planning', data),
  updatePlan: (id, data) => api.patch(`/aop/planning/${id}`, data),
  getLatestPlan: (fiscalYear = 2027) => api.get('/aop/planning', { params: { fiscal_year: fiscalYear } }),
  savePlanVersion: (id, description) => api.post(`/aop/planning/${id}/versions`, null, { params: { description } }),
  getPlanVersions: (id) => api.get(`/aop/planning/${id}/versions`),
};

// Clickstream
export const clickstreamAPI = {
  getEvents:  (params) => api.get('/admin/clickstream', { params }),
  getSummary: ()       => api.get('/admin/clickstream/summary'),
};

const fmtDate = (d) => d ? `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-${String(d.getDate()).padStart(2,'0')}` : undefined;

// Actuals APIs
export const actualsAPI = {
  getFiscalYears:   ()                                      => api.get('/actuals/fiscal-years'),
  getChannels:      (fiscalYear = 2027, dateFrom, dateTo)   => api.get('/actuals/channels',  { params: { fiscal_year: fiscalYear, start_date: fmtDate(dateFrom), end_date: fmtDate(dateTo) } }),
  getChannelDetail: (channelKey, fiscalYear=2027, dateFrom, dateTo) => api.get(`/actuals/channels/${channelKey}`, { params: { fiscal_year: fiscalYear, start_date: fmtDate(dateFrom), end_date: fmtDate(dateTo) } }),
  getOverhead:      (fiscalYear = 2027, dateFrom, dateTo)   => api.get('/actuals/overhead',  { params: { fiscal_year: fiscalYear, start_date: fmtDate(dateFrom), end_date: fmtDate(dateTo) } }),
  importFile:       (file, fiscalYear)            => {
    const fd = new FormData();
    fd.append('file', file);
    return api.post(`/actuals/import?fiscal_year=${fiscalYear}`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
  },
  downloadTemplate: () => api.get('/actuals/template', { responseType: 'blob' }),
};

// Category Mapping APIs
export const categoryMappingAPI = {
  getMappings:         (fiscalYear = 2027) => api.get('/actuals/category-mappings', { params: { fiscal_year: fiscalYear } }),
  getUnmapped:         (fiscalYear = 2027) => api.get('/actuals/unmapped-categories', { params: { fiscal_year: fiscalYear } }),
  saveMappings:        (mappings, fiscalYear = 2027) => api.put('/actuals/category-mappings', { mappings, fiscal_year: fiscalYear }),
  deleteMapping:       (csvCategory, fiscalYear = 2027) => api.delete(`/actuals/category-mappings/${encodeURIComponent(csvCategory)}`, { params: { fiscal_year: fiscalYear } }),
  recalculate:         (fiscalYear = 2027) => api.post('/actuals/recalculate', null, { params: { fiscal_year: fiscalYear } }),
};

// Variance APIs
export const varianceAPI = {
  get: (fiscalYear = 2024) => api.get('/variance', { params: { fiscal_year: fiscalYear } }),
};

// Channel APIs
export const channelAPI = {
  getPerformance: (period = 'ytd') => api.get('/channels', { params: { period } }),
};

// Dashboard APIs
export const dashboardAPI = {
  getKPIs: () => api.get('/dashboard/kpis'),
};

// Health Radar APIs
export const healthAPI = {
  getRadar: () => api.get('/health-radar'),
};

// Simulator APIs
export const simulatorAPI = {
  simulate: (data) => api.post('/simulate', data),
};

// Reports APIs
export const reportsAPI = {
  generate: (data) => api.post('/reports/generate', data),
};

// Admin APIs
export const adminAPI = {
  getUsers: () => api.get('/admin/users'),
  updateStatus: (userId, status) => api.patch(`/admin/users/${userId}/status`, { status }),
  updateRole: (userId, role) => api.patch(`/admin/users/${userId}/role`, { role }),
  deleteUser: (userId) => api.delete(`/admin/users/${userId}`),
  getAccessLogs: () => api.get('/admin/logs/access'),
  getAuditLogs: () => api.get('/admin/logs/audit'),
};

export default api;
